import { useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import api, { apiError } from "@/lib/api";
import { EVENT_TYPES, CITIES, HEAR_ABOUT, ALCOHOL_DISCLAIMER } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const empty = {
  full_name: "", email: "", phone: "", event_date: "", event_city: "", event_address: "",
  event_type: "", indoor_outdoor: "", guest_count: "", adult_count: "", kids_count: "",
  event_start_time: "", event_end_time: "", service_start_time: "", service_end_time: "",
  service_type: "", need_bartender_only: false, need_mobile_bar_rental: false, need_full_service: false,
  preferred_cocktails: "", theme_colors: "", allergies: "", need_kids_drinks: false,
  want_beer: false, want_wine: false, want_champagne: false, budget_range: "", notes: "", how_heard: "",
};

function Field({ label, children, required, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="df-label">{label}{required && <span className="text-destructive"> *</span>}</Label>
      {children}
    </div>
  );
}

export default function QuoteRequest() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      ["guest_count", "adult_count", "kids_count"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      await api.post("/public/quote-request", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <PublicLayout>
        <section className="min-h-[70vh] flex items-center df-container pt-28 pb-20">
          <div className="max-w-lg mx-auto text-center animate-fade-up" data-testid="quote-success">
            <CheckCircle2 className="h-16 w-16 text-olive mx-auto mb-6" />
            <h1 className="font-serif text-4xl mb-4">Thank you!</h1>
            <p className="text-muted-foreground mb-8">Your quote request has been received. The Drunk Fruit team will review your event details and get back to you shortly with a professional, tailored quote.</p>
            <Link to="/" className="btn-dark inline-flex" data-testid="quote-back-home">Back to Home</Link>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="pt-28 pb-20 df-container max-w-3xl">
        <p className="eyebrow mb-3">Request a Quote</p>
        <h1 className="font-serif text-4xl lg:text-5xl mb-3">Tell us about your event</h1>
        <p className="text-muted-foreground mb-10">The more details you share, the more accurate your quote. Fields marked * are required.</p>

        <form onSubmit={submit} className="space-y-10">
          <div>
            <h3 className="font-serif text-2xl mb-4">Your details</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" required><Input required className="df-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="quote-full_name" /></Field>
              <Field label="Email" required><Input required type="email" className="df-input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="quote-email" /></Field>
              <Field label="Phone number"><Input className="df-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="quote-phone" /></Field>
              <Field label="How did you hear about us?">
                <select className="df-input" value={form.how_heard} onChange={(e) => set("how_heard", e.target.value)} data-testid="quote-how_heard">
                  <option value="">Select…</option>
                  {HEAR_ABOUT.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div>
            <h3 className="font-serif text-2xl mb-4">Event details</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Event date"><Input type="date" className="df-input" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} data-testid="quote-event_date" /></Field>
              <Field label="Event type">
                <select className="df-input" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="quote-event_type">
                  <option value="">Select…</option>
                  {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Event city">
                <select className="df-input" value={form.event_city} onChange={(e) => set("event_city", e.target.value)} data-testid="quote-event_city">
                  <option value="">Select…</option>
                  {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Indoor or outdoor?">
                <select className="df-input" value={form.indoor_outdoor} onChange={(e) => set("indoor_outdoor", e.target.value)} data-testid="quote-indoor_outdoor">
                  <option value="">Select…</option>
                  <option>Indoor</option><option>Outdoor</option><option>Both</option>
                </select>
              </Field>
              <Field label="Full event address (optional)" full><Input className="df-input" value={form.event_address} onChange={(e) => set("event_address", e.target.value)} data-testid="quote-event_address" /></Field>
              <Field label="Total guest count"><Input type="number" min="0" className="df-input" value={form.guest_count} onChange={(e) => set("guest_count", e.target.value)} data-testid="quote-guest_count" /></Field>
              <Field label="Adult guests"><Input type="number" min="0" className="df-input" value={form.adult_count} onChange={(e) => set("adult_count", e.target.value)} data-testid="quote-adult_count" /></Field>
              <Field label="Kids / teen guests"><Input type="number" min="0" className="df-input" value={form.kids_count} onChange={(e) => set("kids_count", e.target.value)} data-testid="quote-kids_count" /></Field>
              <Field label="Budget range (optional)"><Input className="df-input" placeholder="e.g. $800 – $1200" value={form.budget_range} onChange={(e) => set("budget_range", e.target.value)} data-testid="quote-budget_range" /></Field>
            </div>
          </div>

          <div>
            <h3 className="font-serif text-2xl mb-4">Timing</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Service start time"><Input type="time" className="df-input" value={form.service_start_time} onChange={(e) => set("service_start_time", e.target.value)} data-testid="quote-service_start_time" /></Field>
              <Field label="Service end time"><Input type="time" className="df-input" value={form.service_end_time} onChange={(e) => set("service_end_time", e.target.value)} data-testid="quote-service_end_time" /></Field>
            </div>
          </div>

          <div>
            <h3 className="font-serif text-2xl mb-4">Service & preferences</h3>
            <Field label="Alcohol service or mocktail-only service?">
              <select className="df-input" value={form.service_type} onChange={(e) => set("service_type", e.target.value)} data-testid="quote-service_type">
                <option value="">Select…</option>
                <option>Alcohol service</option>
                <option>Mocktail-only service</option>
              </select>
            </Field>
            <div className="grid sm:grid-cols-3 gap-3 mt-5">
              {[
                ["need_bartender_only", "Bartender only"],
                ["need_mobile_bar_rental", "Mobile bar rental"],
                ["need_full_service", "Full service (bar + supplies)"],
                ["need_kids_drinks", "Kids drinks"],
                ["want_beer", "Beer served"],
                ["want_wine", "Wine served"],
                ["want_champagne", "Champagne / prosecco"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-3 df-card px-4 py-3 cursor-pointer" data-testid={`quote-${k}`}>
                  <Checkbox checked={form[k]} onCheckedChange={(v) => set(k, !!v)} /> <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-5 mt-5">
              <Field label="Preferred cocktails / mocktails" full><Textarea className="df-input min-h-[80px]" placeholder="e.g. Mango Mojito, Espresso Martini, Spicy Guava Margarita" value={form.preferred_cocktails} onChange={(e) => set("preferred_cocktails", e.target.value)} data-testid="quote-preferred_cocktails" /></Field>
              <Field label="Theme or color scheme"><Input className="df-input" value={form.theme_colors} onChange={(e) => set("theme_colors", e.target.value)} data-testid="quote-theme_colors" /></Field>
              <Field label="Allergies or restrictions"><Input className="df-input" value={form.allergies} onChange={(e) => set("allergies", e.target.value)} data-testid="quote-allergies" /></Field>
              <Field label="Anything else? (notes)" full><Textarea className="df-input min-h-[80px]" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="quote-notes" /></Field>
            </div>
          </div>

          <div className="rounded-2xl bg-clay p-5 text-xs text-muted-foreground">{ALCOHOL_DISCLAIMER}</div>

          <Button type="submit" disabled={loading} className="btn-dark w-full h-14 text-base" data-testid="quote-submit">
            {loading ? "Submitting…" : "Submit Quote Request"} <ArrowRight className="h-4 w-4" />
          </Button>
        </form>
      </section>
    </PublicLayout>
  );
}
